import { Fragment, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { expenseApproverApi, getAccessToken } from '../../lib/api.js';
import { useToast } from '../../hooks/useToast.jsx';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card.jsx';
import Button from '../../components/ui/Button.jsx';
import Textarea from '../../components/ui/Textarea.jsx';
import Modal from '../../components/ui/Modal.jsx';
import ExpenseStatusBadge from '../../components/expense/ExpenseStatusBadge.jsx';
import ReceiptViewer from '../../components/expense/ReceiptViewer.jsx';
import Skeleton from '../../components/ui/Skeleton.jsx';
import { ArrowLeft, CheckCircle, XCircle, Eye, CreditCard, Check, Ban, AlertTriangle, CheckCircle2, Clock, Circle, ChevronRight } from 'lucide-react';

// Stable per-item id — prefer the itemKey that links a line item to its
// receipt(s); fall back to rowKey for legacy rows that have no itemKey.
const itemId = (it) => it.itemKey || it.rowKey;
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
// Compact FX note shown beneath the boxed SAR Total for foreign-currency
// line items — just the rate + the flat 3% fee, highlighted, instead of
// spelling out the full base/fee/total arithmetic. Keyed off the stored
// fee (not currencyType !== 'SAR') so this stays correct regardless of the
// line owner's home currency — the fee is already baked into
// foreignFeeSar by the time it's saved. Falls back to the old SAR check
// for legacy rows saved before foreignFeeSar existed.
function fxNote(item) {
  const isForeign = item.foreignFeeSar != null
    ? item.foreignFeeSar > 0
    : !!item.currencyType && item.currencyType !== 'SAR';
  if (!isForeign) return null;
  const rate = Number(item.exchangeRate || 1);
  return `Rate ${rate.toFixed(4)} · +3% FX fee`;
}

export default function ReviewExpenseReportPage() {
  const { userId, reportId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [report, setReport] = useState(null);
  const [lineItems, setLineItems] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [canApprove, setCanApprove] = useState(false);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [rejectModal, setRejectModal] = useState(false);
  const [rejectNote, setRejectNote] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  // Per-item decisions. Keyed by itemId(item). A value of 'approved' or
  // 'rejected' tints the row; items left undecided are treated as
  // approved at submit time (the approver only needs to flag the bad
  // ones). denyNotes holds the per-item rejection reason collected in
  // the amend-confirmation modal.
  const [marks, setMarks] = useState({});
  const [denyNotes, setDenyNotes] = useState({});
  const [amendModal, setAmendModal] = useState(false);

  useEffect(() => {
    async function load() {
      try {
        // Fetch as approver — use userId from URL to load the correct report
        const token = await getAccessToken();
        const headers = {};
        if (token) { headers['Authorization'] = `Bearer ${token}`; headers['X-ProHR-Token'] = `Bearer ${token}`; }
        const res = await fetch(
          `/api/approver/expenses/${encodeURIComponent(userId)}/${encodeURIComponent(reportId)}`,
          { credentials: 'include', headers }
        ).then((r) => r.json());
        setReport(res.report);
        setLineItems(res.lineItems || []);
        setReceipts(res.receipts || []);
        // canApprove is computed server-side from the same gate the
        // approve/reject endpoints use — keeps UI and authorization aligned.
        setCanApprove(!!res.canApprove);
        // Auto-select the first item so the right pane isn't empty.
        if ((res.lineItems || []).length > 0) setSelectedId(itemId(res.lineItems[0]));
      } catch (err) {
        toast.error(`Failed to load report: ${err.message}`);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [reportId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleApprove = async () => {
    setProcessing(true);
    try {
      await expenseApproverApi.approve(userId, reportId, { note: '' });
      toast.success('Expense report approved');
      navigate('/expense-approvals');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!rejectNote.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    setProcessing(true);
    try {
      await expenseApproverApi.reject(userId, reportId, { note: rejectNote });
      toast.success('Expense report rejected');
      navigate('/expense-approvals');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setProcessing(false);
      setRejectModal(false);
    }
  };

  // Record a decision for the selected item and cycle to the next one so
  // the approver moves through the list. Stays on the last item when
  // there's no next.
  const decide = (decision) => {
    if (!selectedId) return;
    setMarks((m) => ({ ...m, [selectedId]: decision }));
    const idx = lineItems.findIndex((it) => itemId(it) === selectedId);
    const next = lineItems[idx + 1];
    if (next) setSelectedId(itemId(next));
  };

  // "Approve with amendments": send per-item decisions. Approved/undecided
  // items continue in the original claim; rejected items split into a new
  // claim the employee revises. Each rejected item needs a reason.
  const handleAmend = async () => {
    const missing = rejectedItems.some((it) => !(denyNotes[itemId(it)] || '').trim());
    if (missing) {
      toast.error('Add a reason for each item you are sending back');
      return;
    }
    setProcessing(true);
    try {
      const decisions = lineItems.map((it) => {
        const id = itemId(it);
        const isRejected = marks[id] === 'rejected';
        return {
          itemKey: it.itemKey || '',
          rowKey: it.rowKey,
          decision: isRejected ? 'rejected' : 'approved',
          note: isRejected ? (denyNotes[id] || '') : '',
        };
      });
      const res = await expenseApproverApi.approve(userId, reportId, { decisions });
      toast.success(
        res?.splitClaimRef
          ? `Approved with amendments — ${res.splitClaimRef} sent back for revision`
          : 'Expense report approved'
      );
      navigate('/expense-approvals');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setProcessing(false);
      setAmendModal(false);
    }
  };

  // Receipts grouped by the item they belong to (via itemKey), plus any
  // legacy receipts with no itemKey so nothing is hidden from the approver.
  const receiptsByItem = useMemo(() => {
    const map = {};
    for (const r of receipts) {
      if (!r.itemKey) continue;
      (map[r.itemKey] = map[r.itemKey] || []).push(r);
    }
    return map;
  }, [receipts]);
  const unlinkedReceipts = useMemo(
    () => receipts.filter((r) => !r.itemKey),
    [receipts]
  );

  const selectedItem = lineItems.find((it) => itemId(it) === selectedId) || null;
  const selectedReceipts = selectedItem ? (receiptsByItem[selectedItem.itemKey] || []) : [];

  const rejectedItems = lineItems.filter((it) => marks[itemId(it)] === 'rejected');
  const rejectedCount = rejectedItems.length;
  const allRejected = lineItems.length > 0 && rejectedCount === lineItems.length;
  const hasRejections = rejectedCount > 0;

  // Totals split by Company Card vs personal — mirrors the creation page so
  // the approver sees which portion finance has already paid. round2 matches
  // the backend's halala-precision rounding.
  const totalSAR = round2(lineItems.reduce((sum, i) => sum + (Number(i.sarAmount) || 0), 0));
  const companyCardSAR = round2(
    lineItems.reduce((sum, i) => (i.companyCardUsed === true ? sum + (Number(i.sarAmount) || 0) : sum), 0)
  );
  const reimburseSAR = round2(totalSAR - companyCardSAR);
  const hasCompanyCard = companyCardSAR > 0;

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64 w-full rounded-xl" />
      </div>
    );
  }

  if (!report) {
    return <div className="text-muted-foreground">Expense report not found.</div>;
  }

  return (
    <div>
      <button
        onClick={() => navigate('/expense-approvals')}
        className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Queue
      </button>

      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-2xl font-bold tracking-tight">{report.claimRef}</h1>
            <ExpenseStatusBadge status={report.status} />
          </div>
          <p className="text-muted-foreground">
            Submitted by <strong>{report.requesterName}</strong> &middot; {report.claimPurpose}
          </p>
        </div>
        {/* Approval-chain flow chart — who has approved / who's next. */}
        <ApprovalFlow chainSnapshot={report.chainSnapshot} currentStep={report.currentStep} status={report.status} />
      </div>

      {/* Already-resolved notice — approval emails are deep links, and
          clicking one after the request is decided used to show a bare
          read-only page with no explanation. Make it explicit + give a
          way back to the queue. */}
      {report.status !== 'pending' && (
        <div
          className={`mb-6 flex items-start justify-between gap-3 rounded-lg border px-4 py-3 text-sm ${
            report.status === 'approved'
              ? 'border-success/30 bg-success/10 text-success'
              : report.status === 'rejected'
                ? 'border-destructive/30 bg-destructive/10 text-destructive'
                : 'border-border bg-muted text-muted-foreground'
          }`}
        >
          <div>
            <div className="font-semibold">
              {report.status === 'approved'
                ? 'This expense report has already been approved.'
                : report.status === 'rejected'
                  ? 'This expense report was rejected.'
                  : report.status === 'cancelled'
                    ? 'This expense report was cancelled.'
                    : `This expense report is ${report.status}.`}
            </div>
            <div className="mt-0.5 opacity-80">Nothing for you to do here.</div>
          </div>
          <Button variant="secondary" size="sm" onClick={() => navigate('/expense-approvals')} className="shrink-0">
            Back to Queue
          </Button>
        </div>
      )}

      {/* ── Action bar (sticky, top) ── kept at the top so the approver can
          act without scrolling to the bottom. The primary button adapts to
          the per-item marks: all clear → Approve; some denied → Approve
          with Amendments (splits the denied items into a new claim);
          everything denied → only Reject. */}
      {report.status === 'pending' && canApprove && (
        <div className="sticky top-0 z-30 mb-6 flex items-center justify-end gap-3 rounded-lg border border-border bg-card/90 px-4 py-3 backdrop-blur supports-[backdrop-filter]:bg-card/75">
          {hasRejections && !allRejected && (
            <span className="mr-auto text-xs text-muted-foreground">
              {rejectedCount} item{rejectedCount !== 1 ? 's' : ''} marked to send back ·
              {' '}{lineItems.length - rejectedCount} continuing
            </span>
          )}
          <Button variant="danger" onClick={() => setRejectModal(true)} disabled={processing}>
            <XCircle className="h-4 w-4 mr-2" />
            Reject {hasRejections ? 'Whole Report' : ''}
          </Button>
          {allRejected ? null : hasRejections ? (
            <Button onClick={() => setAmendModal(true)} disabled={processing}>
              <CheckCircle className="h-4 w-4 mr-2" />
              Approve with Amendments
            </Button>
          ) : (
            <Button onClick={handleApprove} disabled={processing}>
              <CheckCircle className="h-4 w-4 mr-2" />
              {processing ? 'Processing...' : 'Approve'}
            </Button>
          )}
        </div>
      )}
      {report.status === 'pending' && !canApprove && (
        <div className="mb-6 flex items-start gap-3 rounded-lg border border-border bg-muted/30 p-4 text-sm">
          <Eye className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
          <div>
            <div className="font-medium text-foreground">View only</div>
            <div className="text-muted-foreground mt-0.5">
              You're not the current approver for this report, so you can review the details but can't approve or reject it.
            </div>
          </div>
        </div>
      )}

      {/* ── Summary ── */}
      <Card className="mb-6">
        <CardContent className="p-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <SummaryField label="Requester" value={report.requesterName} />
            <SummaryField label="Department" value={report.requesterDepartment || '—'} />
            <SummaryField label="Location" value={report.officeLocation || '—'} />
            <div>
              <div className="text-muted-foreground text-xs uppercase tracking-wider mb-1">Total (SAR)</div>
              <div className="font-mono font-bold text-profun-cyan">{totalSAR.toFixed(2)}</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── Two-pane: a compact item list (left) + the detail + receipt
          (right). The list is a narrow fixed-width column so the receipt
          gets the bulk of the width; minmax(0,1fr) + min-w-0 lets the
          receipt pane shrink rather than overflow the grid. ── */}
      <div className="mb-6 grid grid-cols-1 gap-6 lg:grid-cols-[260px_minmax(0,1fr)]">
        {/* Left: clickable item list */}
        <Card className="min-w-0">
          <CardHeader>
            <CardTitle className="text-base">Expense Items ({lineItems.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {lineItems.length === 0 ? (
              <p className="text-sm text-muted-foreground">No line items on this report.</p>
            ) : (
              <div className="divide-y divide-border overflow-hidden rounded-lg border border-border">
                {lineItems.map((it, i) => {
                  const id = itemId(it);
                  const isActive = id === selectedId;
                  const mark = marks[id];
                  const count = (receiptsByItem[it.itemKey] || []).length;
                  const tint =
                    mark === 'approved' ? 'bg-profun-teal/5'
                    : mark === 'rejected' ? 'bg-profun-pink/5'
                    : isActive ? 'bg-primary/5' : 'hover:bg-muted/50';
                  const borderColor =
                    isActive ? 'border-l-primary'
                    : mark === 'approved' ? 'border-l-profun-teal'
                    : mark === 'rejected' ? 'border-l-profun-pink'
                    : 'border-l-transparent';
                  return (
                    <button
                      key={id}
                      onClick={() => setSelectedId(id)}
                      className={`flex w-full items-center gap-2 px-2.5 py-1.5 text-left transition-colors border-l-2 ${borderColor} ${tint}`}
                    >
                      <span className="text-[11px] text-muted-foreground tabular-nums w-4 shrink-0">{i + 1}</span>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5">
                          <span className="truncate text-xs font-medium text-foreground">
                            {it.vendorName || it.description || it.expenseTypeId || 'Expense item'}
                          </span>
                          {it.companyCardUsed === true && (
                            <CreditCard className="h-3 w-3 shrink-0 text-profun-amber" title="Company card used" />
                          )}
                          {mark === 'approved' && <Check className="h-3 w-3 shrink-0 text-profun-teal" />}
                          {mark === 'rejected' && <Ban className="h-3 w-3 shrink-0 text-profun-pink" />}
                        </div>
                        <div className="truncate text-[10px] text-muted-foreground">
                          {it.date || 'No date'} &middot; {it.expenseTypeId || '—'}
                          {count > 0 ? ` · ${count} rcpt${count !== 1 ? 's' : ''}` : ''}
                        </div>
                      </div>
                      <span className="shrink-0 font-mono text-[11px] font-semibold text-profun-cyan">
                        {(Number(it.sarAmount) || 0).toFixed(2)}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Right: selected item metadata + its receipt */}
        <Card className="min-w-0">
          <CardHeader className="flex-row items-center justify-between space-y-0">
            <CardTitle className="text-base">Item Detail</CardTitle>
            {/* Per-item decision — top-right so it's visible without
                scrolling past the receipt. */}
            {selectedItem && report.status === 'pending' && canApprove && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => decide('approved')}
                  className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-semibold transition-colors ${
                    marks[selectedId] === 'approved'
                      ? 'border-profun-teal bg-profun-teal text-white'
                      : 'border-profun-teal/50 text-profun-teal hover:bg-profun-teal/10'
                  }`}
                >
                  <Check className="h-3.5 w-3.5" /> Approve
                </button>
                <button
                  onClick={() => decide('rejected')}
                  className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-semibold transition-colors ${
                    marks[selectedId] === 'rejected'
                      ? 'border-profun-pink bg-profun-pink text-white'
                      : 'border-profun-pink/50 text-profun-pink hover:bg-profun-pink/10'
                  }`}
                >
                  <Ban className="h-3.5 w-3.5" /> Deny
                </button>
              </div>
            )}
          </CardHeader>
          <CardContent>
            {!selectedItem ? (
              <p className="text-sm text-muted-foreground">Select an item to see its details and receipt.</p>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <SummaryField label="Date" value={selectedItem.date || '—'} />
                  <SummaryField label="Type" value={selectedItem.expenseTypeId || '—'} />
                  <SummaryField label="Vendor" value={selectedItem.vendorName || '—'} />
                  <SummaryField label="Charge Account" value={selectedItem.chargeAccount || '—'} />
                  <div className="col-span-2">
                    <div className="text-muted-foreground text-xs uppercase tracking-wider mb-1">Description</div>
                    <div className="font-medium text-foreground break-words">
                      {selectedItem.description || <span className="text-muted-foreground/50 italic font-normal">No description</span>}
                    </div>
                  </div>
                  <div>
                    <div className="text-muted-foreground text-xs uppercase tracking-wider mb-1">Amount</div>
                    <div className="font-mono font-medium text-foreground">
                      {selectedItem.currencyType || 'SAR'} {(Number(selectedItem.currencyAmount) || 0).toFixed(2)}
                    </div>
                  </div>
                  {/* Final SAR total — the highlighted figure, with a
                      compact rate + fee note beneath it (no full arithmetic). */}
                  <div className="col-span-2 rounded-lg border border-profun-cyan/20 bg-profun-cyan/5 px-3 py-2">
                    <div className="text-muted-foreground text-xs uppercase tracking-wider mb-0.5">SAR Total</div>
                    <div className="font-mono text-lg font-bold text-profun-cyan">{(Number(selectedItem.sarAmount) || 0).toFixed(2)}</div>
                    {fxNote(selectedItem) && (
                      <div className="mt-1.5 inline-flex items-center rounded-full bg-profun-cyan/10 px-2 py-0.5 text-[11px] font-medium text-profun-cyan">
                        {fxNote(selectedItem)}
                      </div>
                    )}
                  </div>
                </div>

                {selectedItem.companyCardUsed === true && (
                  <div className="flex items-center gap-2 rounded-md bg-profun-amber/10 px-3 py-2 text-xs text-profun-amber">
                    <CreditCard className="h-3.5 w-3.5 shrink-0" />
                    Paid on company card — already paid, do not reimburse.
                  </div>
                )}

                <div>
                  <div className="text-muted-foreground text-xs uppercase tracking-wider mb-2">Receipt</div>
                  <ReceiptViewer
                    receipts={selectedReceipts}
                    emptyMessage="No receipt linked to this item."
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ── Legacy / unmatched receipts (no item link) ── */}
      {unlinkedReceipts.length > 0 && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-base">Other Receipts ({unlinkedReceipts.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="mb-3 text-xs text-muted-foreground">
              These receipts aren't linked to a specific line item (uploaded before item-level linking, or added separately).
            </p>
            <ReceiptViewer receipts={unlinkedReceipts} emptyMessage="No other receipts." />
          </CardContent>
        </Card>
      )}

      {/* ── Totals breakdown (Company Card vs Personal Reimbursement) ── */}
      <Card className="mb-6">
        <CardContent className="p-6">
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Total (SAR)</span>
              <span className="font-mono font-semibold">{totalSAR.toFixed(2)}</span>
            </div>
            {hasCompanyCard && (
              <>
                <div className="flex justify-between text-sm pl-3">
                  <span className="text-muted-foreground">
                    • Company Card Used (already paid — do not reimburse)
                  </span>
                  <span className="font-mono">{companyCardSAR.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-base font-bold pt-2 border-t border-border">
                  <span>Personal Reimbursement</span>
                  <span className="text-profun-cyan font-mono">{reimburseSAR.toFixed(2)}</span>
                </div>
                <p className="text-[11px] text-muted-foreground pt-1">
                  Finance: only the Personal Reimbursement amount should be paid out to the
                  employee's personal account. The Company Card portion was already paid by
                  the company.
                </p>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Reject modal */}
      <Modal isOpen={rejectModal} onClose={() => setRejectModal(false)} title="Reject Expense Report">
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Please provide a reason for rejecting this expense report.
          </p>
          <Textarea
            label="Reason for rejection"
            value={rejectNote}
            onChange={(e) => setRejectNote(e.target.value)}
            placeholder="Explain why this report is being rejected..."
            rows={3}
          />
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setRejectModal(false)}>Cancel</Button>
            <Button variant="danger" onClick={handleReject} disabled={processing}>
              {processing ? 'Rejecting...' : 'Confirm Rejection'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Approve-with-amendments confirmation — collect a reason per item
          being sent back before committing the split. */}
      <Modal isOpen={amendModal} onClose={() => setAmendModal(false)} title="Approve with Amendments" width="560px">
        <div className="space-y-4">
          <div className="flex items-start gap-3 rounded-lg border border-profun-amber/30 bg-profun-amber/5 p-3 text-sm">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-profun-amber" />
            <div className="text-muted-foreground">
              The {lineItems.length - rejectedCount} approved item{lineItems.length - rejectedCount !== 1 ? 's' : ''} will continue through approval on
              {' '}<strong className="text-foreground">{report.claimRef}</strong>. The {rejectedCount} item{rejectedCount !== 1 ? 's' : ''} below will be
              split into a new claim and returned to <strong className="text-foreground">{report.requesterName}</strong> to revise and resubmit.
            </div>
          </div>
          <div className="space-y-3 max-h-[320px] overflow-y-auto">
            {rejectedItems.map((it) => {
              const id = itemId(it);
              return (
                <div key={id} className="rounded-lg border border-border p-3">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-sm font-medium text-foreground">
                      {it.vendorName || it.description || it.expenseTypeId || 'Expense item'}
                    </span>
                    <span className="font-mono text-xs text-profun-cyan">{(Number(it.sarAmount) || 0).toFixed(2)} SAR</span>
                  </div>
                  <Textarea
                    value={denyNotes[id] || ''}
                    onChange={(e) => setDenyNotes((n) => ({ ...n, [id]: e.target.value }))}
                    placeholder="Why is this item being sent back? (required)"
                    rows={2}
                  />
                </div>
              );
            })}
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setAmendModal(false)}>Cancel</Button>
            <Button onClick={handleAmend} disabled={processing}>
              {processing ? 'Processing...' : 'Confirm & Submit'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

function SummaryField({ label, value }) {
  return (
    <div>
      <div className="text-muted-foreground text-xs uppercase tracking-wider mb-1">{label}</div>
      <div className="font-medium text-foreground break-words">{value}</div>
    </div>
  );
}

function stepLabel(step) {
  if (step.label) return step.label;
  if (step.type === 'manager') return 'Direct Manager';
  if (step.type === 'role') return `Role: ${step.value}`;
  return step.value || 'Approver';
}

// Compact horizontal approval-chain flow chart for the header — shows each
// approver in the chain and whether they've approved, rejected, are up next,
// or are still to come. Renders nothing when there's no chain.
function ApprovalFlow({ chainSnapshot, currentStep, status }) {
  if (!Array.isArray(chainSnapshot) || chainSnapshot.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-1">
      {chainSnapshot.map((step, i) => {
        const isApproved = step.status === 'approved';
        const isRejected = step.status === 'rejected';
        const isCurrent = i === (currentStep || 0) && status === 'pending';
        const Icon = isApproved ? CheckCircle2 : isRejected ? XCircle : isCurrent ? Clock : Circle;
        const color = isApproved ? 'text-profun-teal'
          : isRejected ? 'text-profun-pink'
          : isCurrent ? 'text-profun-amber'
          : 'text-muted-foreground/40';
        const state = isApproved ? 'Approved' : isRejected ? 'Rejected' : isCurrent ? 'Awaiting decision' : 'Pending';
        const decided = step.decidedAt ? ` · ${new Date(step.decidedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : '';
        const decidedByLabel = (isApproved || isRejected) && step.decidedBy ? ` by ${step.decidedBy}` : '';
        return (
          <Fragment key={i}>
            {i > 0 && <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground/40" />}
            <div
              className={`flex items-center gap-1.5 rounded-md border px-2 py-1 ${isCurrent ? 'border-profun-amber/40 bg-profun-amber/5' : 'border-border bg-card'}`}
              title={`${stepLabel(step)} — ${state}${decidedByLabel}${decided}`}
            >
              <Icon className={`h-4 w-4 shrink-0 ${color}`} />
              <span className="max-w-[140px] truncate text-xs font-medium text-foreground">{stepLabel(step)}</span>
            </div>
          </Fragment>
        );
      })}
    </div>
  );
}
